import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import BookingCalendarSkeleton from '@/components/admin/booking/BookingCalendarSkeleton';
import BookingDayGrid from '@/components/admin/booking/BookingDayGrid';
import BookingDetailDrawer from '@/components/admin/booking/BookingDetailDrawer';
import BookingToolbar from '@/components/admin/booking/BookingToolbar';
import BookingWeekGrid from '@/components/admin/booking/BookingWeekGrid';
import CustomSessionBanner from '@/components/admin/booking/CustomSessionBanner';
import FieldStatusMenu from '@/components/admin/booking/FieldStatusMenu';
import ManualBookingDrawer from '@/components/admin/booking/ManualBookingDrawer';
import { useBookingCalendar } from '@/components/admin/booking/useBookingCalendar';
import { cancelCalendarOrder, createManualBooking, markOrderPaid } from '@/services/bookingCalendarService';

const BookingSchedule = () => {
  const calendar = useBookingCalendar();

  const run = async (action: () => Promise<void>, success: string) => {
    try {
      await action();
      showToast.success(success);
      calendar.closeDetail();
      calendar.closeManual();
      await calendar.reload();
    } catch (error) {
      showToast.error(getApiErrorMessage(error, 'Aksi booking gagal'));
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-8xl mx-auto pb-10">
      {/* Header Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-[#0E1726]">Booking</h1>

        <div className="flex items-center gap-3">
          <FieldStatusMenu
            fields={calendar.catalog.length ? calendar.catalog : calendar.fields}
            onSelect={(field, mode) => {
              void calendar.setMode(field, mode);
            }}
          />
          <button
            type="button"
            onClick={calendar.openManual}
            className="h-10 px-4 sm:px-5 inline-flex items-center gap-2 rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white text-xs sm:text-sm font-semibold shadow-xs transition-colors shrink-0 cursor-pointer"
          >
            <Icon icon="ph:plus-bold" className="w-4 h-4" />
            <span>Tambah Booking</span>
          </button>
        </div>
      </div>

      {/* Main Calendar Card */}
      <div className="rounded-2xl border border-[#E0E6ED] bg-white p-5 sm:p-6 shadow-2xs">
        <BookingToolbar
          rangeLabel={calendar.rangeLabel}
          view={calendar.view}
          fields={calendar.catalog.length ? calendar.catalog : calendar.fields}
          fieldSlug={calendar.fieldSlug}
          onShift={calendar.shift}
          onToday={calendar.today}
          onView={calendar.setView}
          onField={calendar.setField}
        />

        {calendar.draft && (
          <CustomSessionBanner
            fieldName={calendar.draft.fieldName}
            count={calendar.draft.keys.length}
            onCancel={calendar.cancelDraft}
            onSave={() => {
              void calendar.saveDraft();
            }}
          />
        )}

        <div className="relative mt-2">
          {calendar.loading || calendar.refreshing ? (
            <BookingCalendarSkeleton
              view={calendar.view}
              days={calendar.weekDays}
              hours={calendar.hours}
              now={calendar.now}
              fieldCount={calendar.catalog.length || calendar.fields.length || 4}
            />
          ) : calendar.data && calendar.fields.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 py-16 text-center">
              <p className="text-sm font-medium text-gray-500">Belum ada sesi yang bisa dibooking</p>
            </div>
          ) : calendar.data && calendar.fields.length > 0 && calendar.view === 'week' ? (
            <BookingWeekGrid
              fields={calendar.fields}
              days={calendar.weekDays}
              hours={calendar.hours}
              stepMinutes={calendar.slotStepMinutes}
              selected={calendar.selectedKeys}
              now={calendar.now}
              draft={calendar.draft}
              onSlot={(field, block) =>
                calendar.onSlot(field, { ...block.slots[0], start: block.start, end: block.end })
              }
              onFocusDay={calendar.focusDay}
            />
          ) : calendar.data && calendar.fields.length > 0 && calendar.view === 'day' ? (
            <BookingDayGrid
              fields={calendar.fields}
              date={calendar.cursor}
              hours={calendar.hours}
              stepMinutes={calendar.slotStepMinutes}
              selected={calendar.selectedKeys}
              now={calendar.now}
              onSlot={(field, block) =>
                calendar.onSlot(field, { ...block.slots[0], start: block.start, end: block.end })
              }
            />
          ) : null}
        </div>

        <ManualBookingDrawer
          target={calendar.manual}
          arenaSlug={calendar.arenaSlug}
          fields={calendar.catalog.length ? calendar.catalog : calendar.fields}
          onClose={calendar.closeManual}
          onSubmit={(payload) =>
            run(() => createManualBooking(calendar.arenaSlug, payload), 'Booking berhasil disimpan')
          }
        />

        <BookingDetailDrawer
          target={calendar.detail}
          onClose={calendar.closeDetail}
          onPaid={(orderNumber) =>
            run(() => markOrderPaid(calendar.arenaSlug, orderNumber), 'Pembayaran ditandai lunas')
          }
          onCancel={(invoiceNumber) =>
            run(() => cancelCalendarOrder(calendar.arenaSlug, invoiceNumber), 'Booking dibatalkan')
          }
        />
      </div>
    </div>
  );
};

export default BookingSchedule;
